import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { PersonalInfoStep } from './steps/PersonalInfoStep'
import { AppOverviewStep } from './steps/AppOverviewStep'
import { AccountSetupStep } from './steps/AccountSetupStep'
import { CategorySetupStep } from './steps/CategorySetupStep'

import { BudgetIntroStep } from './steps/BudgetIntroStep'
import { GoalSettingStep } from './steps/GoalSettingStep'
import { CompletionStep } from './steps/CompletionStep'
import { useOnboardingStore } from '@/store/onboardingStore'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'

export function OnboardingWizard() {
  const [currentStep, setCurrentStep] = useState(0)
  const { isComplete, hasCheckedProfile, cleanup } = useOnboardingStore()
  const { user } = useAuth()
  const { t } = useTranslation()

  // Clean up onboarding state when component unmounts or user changes
  useEffect(() => {
    if (!user) {
      cleanup()
    }
  }, [user, cleanup])

  // Don't show if:
  // 1. User is not logged in
  // 2. Profile hasn't been checked yet (let dashboard handle the loading)
  // 3. Onboarding is complete
  if (!user?.id || !hasCheckedProfile || isComplete) {
    return null
  }

  const steps = [
    {
      title: t('onboarding.appOverview.title', 'Welcome to Flows.Cash'),
      subtitle: t('onboarding.appOverview.subtitle', 'Your Complete Financial Command Center'),
      description: t('onboarding.appOverview.description', 'Let\'s explore what makes Flows.Cash powerful'),
      component: <AppOverviewStep onNext={() => setCurrentStep(1)} onBack={() => setCurrentStep(0)} />
    },
    {
      title: t('onboarding.personalInfo.title', 'Set Up Your Profile'),
      subtitle: t('onboarding.personalInfo.subtitle', 'Personalize your experience'),
      description: t('onboarding.personalInfo.description', 'Tell us a bit about yourself to customize your experience'),
      component: <PersonalInfoStep onNext={() => setCurrentStep(2)} />
    },
    {
      title: t('onboarding.accountSetup.title', 'Add Your First Account'),
      subtitle: t('onboarding.accountSetup.subtitle', 'Start tracking your money'),
      description: t('onboarding.accountSetup.description', 'Add your bank account, wallet, or any other account to begin'),
      component: <AccountSetupStep onNext={() => setCurrentStep(3)} onBack={() => setCurrentStep(1)} />
    },
    {
      title: t('onboarding.categorySetup.title', 'Organize Your Spending'),
      subtitle: t('onboarding.categorySetup.subtitle', 'Customize your categories'),
      description: t('onboarding.categorySetup.description', 'Set up categories that match your lifestyle'),
      component: <CategorySetupStep onNext={() => setCurrentStep(4)} />
    },
    {
      title: t('onboarding.budgetIntro.title', 'Set Your First Budget'),
      subtitle: t('onboarding.budgetIntro.subtitle', 'Take control of your spending'),
      description: t('onboarding.budgetIntro.description', 'Create budgets to stay on track with your financial goals'),
      component: <BudgetIntroStep onNext={() => setCurrentStep(5)} onSkip={() => setCurrentStep(5)} />
    },
    {
      title: t('onboarding.goalSetting.title', 'Set Your Financial Goals'),
      subtitle: t('onboarding.goalSetting.subtitle', 'Dream big, save smart'),
      description: t('onboarding.goalSetting.description', 'Set savings goals and track your progress'),
      component: <GoalSettingStep onNext={() => setCurrentStep(6)} onSkip={() => setCurrentStep(6)} />
    },
    {
      title: t('onboarding.completion.title', 'You\'re All Set!'),
      subtitle: t('onboarding.completion.subtitle', 'Welcome to your financial journey'),
      description: t('onboarding.completion.description', 'Time to explore and take control of your finances'),
      component: <CompletionStep />
    }
  ]

  const progress = ((currentStep + 1) / steps.length) * 100

  return (
    <Dialog open={true} modal>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] p-0 gap-0 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-xl flex flex-col">
        {/* Header with Progress and Navigation */}
        <DialogTitle asChild>
          <div className="flex-shrink-0 p-6 sm:p-8 border-b border-border dark:border-border-dark bg-background-secondary/50 dark:bg-background-dark-secondary/50 backdrop-blur-lg">
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
                {steps[currentStep].title}
              </h2>
                  <p className="text-base text-secondary dark:text-primary-dark font-medium">
                {steps[currentStep].subtitle}
              </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark rounded-xl backdrop-blur-lg">
                    <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">{currentStep + 1}</span>
                    <span className="text-sm text-text-secondary dark:text-text-dark-secondary">/</span>
                    <span className="text-sm text-text-secondary dark:text-text-dark-secondary">{steps.length}</span>
                  </div>
                  {/* Navigation Controls */}
                  {currentStep < steps.length - 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                        disabled={currentStep === 0}
                        className="flex items-center gap-1 h-9 px-3 hover:bg-secondary/10 dark:hover:bg-primary-dark/10 hover:text-secondary dark:hover:text-primary-dark rounded-lg transition-all duration-200"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        {t('common.back', 'Back')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setCurrentStep(currentStep + 1)}
                        className="flex items-center gap-1 h-9 px-3 bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        {t('common.next', 'Next')}
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="space-y-3">
                <Progress 
                  value={progress} 
                  className="h-2 bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark"
                />
                <div className="flex items-center justify-between">
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    {Math.round(progress)}% {t('common.complete', 'complete')}
                  </p>
                  <p className="text-sm text-secondary dark:text-primary-dark font-medium">
                    {t('onboarding.progress.stepsRemaining', 'Steps remaining: {{count}}', { count: steps.length - currentStep - 1 })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogDescription className="sr-only">
          {steps[currentStep].description}
        </DialogDescription>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-6 sm:p-8">
          {steps[currentStep].component}
          </div>
        </div>

      </DialogContent>
    </Dialog>
  )
} 